import React from "react";
import { useClient } from "urql";
import AccountActions from "../components/actions";
import { SearchContext } from "../../common/context/search";
import {
  Application,
  AccountsDocument,
  AccountState,
  AccountType,
  useAccountsQuery,
} from "../../graphql/generated";
import { classNames } from "../../common/utils/classnames";
import AccountCard from "../components/card";
import CommonSwitch from "../../common/components/switch";
import { Transition } from "@headlessui/react";
import Loading from "../../common/components/loading";
import Error from "../../common/components/error";
import { useTitle } from "../../common/utils/title";

const AccountList: React.FC = () => {
  useTitle("Accounts");
  const [loading, setLoading] = React.useState(false);
  const graphqlClient = useClient();

  const {
    searchBounced,
    applications,
    applicationsDispatch,
    accountTypes,
    accountTypesDispatch,
    accountStates,
    accountStatesDispatch,
    excludeTest,
    excludeTestDispatch,
  } = React.useContext(SearchContext);

  const [{ data, error, fetching }, executeQuery] = useAccountsQuery({
    variables: {
      first: 20,
      applications: applications,
      types: accountTypes,
      states: accountStates,
      search: searchBounced,
      excludeTest: excludeTest,
    },
  });

  const fetchMore = React.useCallback(async () => {
    if (data?.accounts.pageInfo.hasNextPage) {
      setLoading(true);
      await graphqlClient
        .query(AccountsDocument, {
          first: 20,
          after: data.accounts.pageInfo.endCursor,
          applications: applications,
          types: accountTypes,
          states: accountStates,
          search: searchBounced,
          excludeTest: excludeTest,
        })
        .toPromise();
      setLoading(false);
    }
  }, [
    data,
    graphqlClient,
    accountTypes,
    accountStates,
    searchBounced,
    excludeTest,
  ]);

  return (
    <>
      <Loading show={!data && !error} />
      <Error error={error} />
      <Transition
        show={!!data}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-4">
          <div className="sm:flex sm:items-center sm:justify-between">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                Accounts
                <span className="text-sm text-gray-500 px-2">
                  {fetching ? "Loading..." : `Total: ${data?.accounts.total}`}
                </span>
              </h2>
              <div className="mt-1 grid grid-cols-1 gap-0">
                <div className="mt-2 flex items-center text-sm text-gray-500">
                  <span className="w-18">Application:</span>
                  <CommonSwitch
                    enabled={applications.indexOf(Application.Zenlist) > -1}
                    toggle={() =>
                      applicationsDispatch({ application: Application.Zenlist })
                    }
                    label="Zenlist"
                  />
                  <CommonSwitch
                    enabled={applications.indexOf(Application.RealtorPlus) > -1}
                    toggle={() =>
                      applicationsDispatch({ application: Application.RealtorPlus })
                    }
                    label="Realtor+"
                  />
                </div>
                <div className="mt-2 flex items-center text-sm text-gray-500">
                  <span className="w-16">State:</span>
                  <CommonSwitch
                    enabled={accountStates.indexOf(AccountState.Active) > -1}
                    toggle={() =>
                      accountStatesDispatch({ state: AccountState.Active })
                    }
                    label="Active"
                  />
                  <CommonSwitch
                    enabled={
                      accountStates.indexOf(AccountState.Deactivating) > -1
                    }
                    toggle={() =>
                      accountStatesDispatch({
                        state: AccountState.Deactivating,
                      })
                    }
                    label="Deactivating"
                  />
                  <CommonSwitch
                    enabled={accountStates.indexOf(AccountState.Unlinked) > -1}
                    toggle={() =>
                      accountStatesDispatch({ state: AccountState.Unlinked })
                    }
                    label="Unlinked"
                  />
                </div>
                <div className="mt-2 flex items-center text-sm text-gray-500">
                  <span className="w-16">Type:</span>
                  <CommonSwitch
                    enabled={accountTypes.indexOf(AccountType.Agent) > -1}
                    toggle={() =>
                      accountTypesDispatch({ type: AccountType.Agent })
                    }
                    label="Agent"
                  />
                  <CommonSwitch
                    enabled={accountTypes.indexOf(AccountType.Member) > -1}
                    toggle={() =>
                      accountTypesDispatch({ type: AccountType.Member })
                    }
                    label="Member"
                  />
                  <CommonSwitch
                    enabled={accountTypes.indexOf(AccountType.Draft) > -1}
                    toggle={() =>
                      accountTypesDispatch({ type: AccountType.Draft })
                    }
                    label="Draft"
                  />
                  <CommonSwitch
                    enabled={accountTypes.indexOf(AccountType.Lender) > -1}
                    toggle={() =>
                      accountTypesDispatch({ type: AccountType.Lender })
                    }
                    label="Lender"
                  />
                </div>
              </div>
              <div className="mt-1 grid grid-cols-1 gap-0">
                <div className="mt-2 flex items-center text-sm text-gray-500">
                  <span className="w-40">Exclude Test Accounts:</span>
                  <CommonSwitch
                    enabled={excludeTest}
                    toggle={() => excludeTestDispatch({ value: !excludeTest })}
                    label="Exclude"
                  />
                </div>
              </div>
            </div>
            <div className="mt-5 flex sm:mt-0 sm:mr-4">
              <AccountActions executeQuery={() => executeQuery()} />
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-4">
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="p-4">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {data?.accounts?.edges?.map((account) => (
                  <AccountCard account={account?.node} key={account?.node.id} />
                ))}
              </div>
            </div>
          </div>
          {data &&
            data?.accounts.total > (data?.accounts?.edges?.length ?? 0) && (
              <div className="min-w-full divide-y divide-gray-200">
                <div className="p-4">
                  <button
                    className={classNames(
                      loading
                        ? "bg-gray-500 text-white"
                        : "bg-zenlist-500 hover:bg-zenlist-700 text-white",
                      "font-bold py-2 px-4 rounded"
                    )}
                    onClick={() => fetchMore()}
                    disabled={loading}
                  >
                    Fetch More
                  </button>
                </div>
              </div>
            )}
        </div>
      </Transition>
    </>
  );
};

export default AccountList;
